/**
 * Per-page selected event (see helpers/selectedEvent.ts).
 *
 * `byPage[pageKey]` is the event chosen on that page in this session (an
 * explicit choice, a value restored from localStorage, or the default).
 * `activePage` is the page currently open; `generalFilters.event` mirrors
 * `byPage[activePage]` so the existing listing hooks keep reading it.
 * Kept out of `generalFilters` on purpose: that object is snapshotted and
 * JSON-stringified as cache keys by several pages.
 */

// actions (also handled by the generalFilters reducer, which mirrors `event`)
export const ACTIVATE_PAGE_EVENT = 'pageEvents/ACTIVATE';
export const SET_PAGE_EVENT = 'pageEvents/SET';
// generalFilters actions that may carry `event` (legacy writers)
const GENERAL_FILTERS_SET = 'generalFilters/SET';
const GENERAL_FILTERS_SET_MULTIPLE = 'generalFilters/SET_MULTIPLE';

export interface PageEventsState {
  activePage: string;
  byPage: Record<string, string>;
}

const DEFAULT_STATE: PageEventsState = {
  activePage: '',
  byPage: {},
};

const withPageEvent = (
  state: PageEventsState,
  pageKey: string,
  eventId: string,
): PageEventsState => {
  if (!pageKey || state.byPage[pageKey] === eventId) return state;
  return { ...state, byPage: { ...state.byPage, [pageKey]: eventId } };
};

const reducer = (
  state: PageEventsState = DEFAULT_STATE,
  action: any = {},
): PageEventsState => {
  switch (action.type) {
    case ACTIVATE_PAGE_EVENT: {
      const next = withPageEvent(state, action.pageKey, action.eventId || '');
      return next.activePage === action.pageKey
        ? next
        : { ...next, activePage: action.pageKey };
    }
    case SET_PAGE_EVENT:
      return withPageEvent(state, action.pageKey, action.eventId || '');
    // A page that still writes `generalFilters.event` directly changes the
    // selection of the page that is open, never the other pages.
    case GENERAL_FILTERS_SET:
      return action.name === 'event'
        ? withPageEvent(state, state.activePage, action.value || '')
        : state;
    case GENERAL_FILTERS_SET_MULTIPLE:
      return action.values && 'event' in action.values
        ? withPageEvent(state, state.activePage, action.values.event || '')
        : state;
    default:
      return state;
  }
};

export default reducer;

/** Make `pageKey` the open page and mirror its event to generalFilters. */
export const activatePageEvent = (pageKey: string, eventId: string) => ({
  type: ACTIVATE_PAGE_EVENT,
  pageKey,
  eventId,
});

/**
 * Change one page's event. `mirror` = that page is the open one, so
 * `generalFilters.event` follows (callers pass `activePage === pageKey`).
 */
export const setPageEvent = (
  pageKey: string,
  eventId: string,
  mirror: boolean,
) => ({
  type: SET_PAGE_EVENT,
  pageKey,
  eventId,
  mirror,
});
